import React, {useEffect} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import cs from 'classnames';
import moment from 'moment-tz';
import {DefaultRootState, useSelector} from 'react-redux';
import {getProcedureTaskCore, getProcessTaskCore} from 'sweet-taskinfo';

import {loadComments} from 'app/comments/actions';
import {ListMessage} from 'app/components/general/ListMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {confirmOnLeaveEnhancer} from 'app/general/confirmOnLeaveEnhancer';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {showTaskUpgradeAvailableAlertBoxIfAvailable} from 'app/general/structures/document-alerts/TaskUpgradeAvailableAlertBox';
import {SPPage} from 'app/general/structures/SPPage';
import {VideoManagerProvider} from 'app/general/VideoManagerProvider';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {setSideDiagramState} from 'app/procedures/actions';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {
  ProcedureDiagramMobile,
  ProcedureDiagramSidebar,
  ToggleProcedureDiagramMobileButton,
} from 'app/procedures/components/ProcedureDiagram';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {loadProcedureByHashid} from 'app/procedures/hocs';
import {normalizeSteps} from 'app/procedures/utils';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useTaskInstance} from 'app/tasks/api';
import {TaskDetailPageSidebar} from 'app/tasks/components/TaskDetailPageSidebar';
import {useFetchTaskLanes} from 'app/tasks/task-lanes';
import {useTaskUpdate} from 'app/tasks/task-update-hook';
import {useDetectPrint} from 'app/utils/browser';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {onlyUpdateForKeys} from 'app/utils/react-utils';
import {useAsyncActions} from 'app/utils/redux';

import {ProcessNavButtons} from './ProcessNavButtons';
import {TaskDetailStepsList} from './TaskDetailStepsList';
import {TaskPageActionButtons} from './TaskPageActionButtons';
import {ViewTaskMetaData} from './ViewTaskMetaData';

import {
  AuthenticatedUserSerializedType,
  ProcedureSerializedType,
  TaskInstanceSerializedType,
  UpdateTaskPayloadType,
} from 'sweet-types';

import type {ConfirmOnLeaveProps} from 'app/general/confirmOnLeaveEnhancer';
import type {LoadByHashidInjectedProps} from 'app/procedures/hocs';
import type {Map} from 'immutable';
import type {WithRouterProps} from 'react-router';

interface PageParams {
  hashid: string;
  taskid: string;
  processid: string | undefined;
}

interface Props
  extends WithRouterProps<PageParams>,
    ConfirmOnLeaveProps,
    LoadByHashidInjectedProps<ProcedureSerializedType> {
  route: {
    props: {
      showComments?: boolean;
      showActivity?: boolean;
      showTasks?: boolean;
    };
  };
}

export const TaskDetailPage = confirmOnLeaveEnhancer(() =>
  gt('Your changes are still being saved. Are you sure you want to leave?')
)(
  loadProcedureByHashid(
    onlyUpdateForKeys(
      ({
        params,
        router,
        route,
        location,
        fetchedByHashid: {fetchObject: fetchProcedure, object: procedure},
        shouldConfirmOnLeave,
      }: Props) => {
        const {closedSideDiagram, timezone} = useSelector((state) =>
          taskDataSelector(state, {procedureId: procedure.id})
        );

        const {updateTaskInstance} = useTaskUpdate();

        const {data: task, refetch, error} = useTaskInstance(params.taskid);

        const taskDeleted = React.useMemo(() => {
          return error != null && 'status' in error && error.status === 404;
        }, [error]);

        const taskCore =
          task != null ? getProcedureTaskCore(task, params.hashid) : null;

        const printing = useDetectPrint();

        const actions = useAsyncActions({loadComments, setSideDiagramState});

        // use the same signature of old actions.updateTaskInstance to quickly replace the
        // old one with this one
        const preparedUpdateTaskInstance = (
          task: TaskInstanceSerializedType,
          update: UpdateTaskPayloadType
        ) => {
          return updateTaskInstance({url: task.url, update});
        };

        const [warnIncomplete, setWarnIncomplete] = React.useState(false);

        const [showOverlayDiagram, setShowOverlayDiagram] =
          React.useState(false);
        const toggleDiagramOverlay = () =>
          setShowOverlayDiagram((show) => !show);

        const taskName = task?.description || task?.name || '';

        const pageTitle = React.useMemo(() => {
          if (task != null) {
            const due = moment(task.due).tz(timezone).format('lll');
            return gt('%(name)s | Task | %(due)s', {
              name: taskName,
              due,
            });
          }
          return '';
        }, [task?.due, timezone, taskName]);

        useEffect(() => {
          // Scroll the step into view if someone loads page with it in the fragment.
          const hash = location.hash;
          if (hash != '' && task) {
            const id = hash.substring(1);
            handleDiagramStepClick(id);
          }
        }, [task]);

        const isTaskBlocked: boolean = React.useMemo((): boolean => {
          // checks if the procedure is part of a sequential process-task
          // and if it's can be marked as completed

          if (taskCore == null || task == null || params.processid == null) {
            return false;
          }

          // let's find out if the procedure is available to checked off or is blocked
          const processTaskCore = getProcessTaskCore(task);

          if (processTaskCore.hasVersion !== true) {
            return false;
          }

          const {activeNodes, completedPath} =
            processTaskCore.state.progressInfo;
          return (
            completedPath.indexOf(params.hashid) === -1 &&
            !activeNodes.has(params.hashid)
          );
        }, []);

        React.useEffect(() => {
          actions.loadComments(parseInt(params.taskid), 'taskinstance');
        }, [params.taskid]);

        useFetchTaskLanes(taskCore);

        useModelChangeSubscription(
          (message, {runWhenAny, modelIdMatch, allRelatedMatch}) => {
            const taskInstanceId = parseInt(params.taskid);

            if (task != null) {
              runWhenAny(
                message,
                [
                  modelIdMatch('taskinstance', taskInstanceId),
                  modelIdMatch('tasktemplate', task.template.id),
                ],
                () => fetchTaskinstanceData()
              );

              runWhenAny(
                message,
                [
                  allRelatedMatch('comment', {
                    content_type: 'taskinstance',
                    id: taskInstanceId,
                  }),
                ],
                () => actions.loadComments(taskInstanceId, 'taskinstance')
              );

              if (task.assigned_version_content != null) {
                const {content_type, id} = task.assigned_version_content;
                const assignedObject = task.template.assigned_object;
                runWhenAny(
                  message,
                  [
                    modelIdMatch(content_type, id),
                    allRelatedMatch(content_type, {
                      content_type: assignedObject.content_type,
                      id: assignedObject.id,
                    }),
                  ],
                  () => {
                    if ('id' in message && typeof message.id == 'number') {
                      fetchTaskinstanceData();
                    }
                  }
                );
              }
            }

            // procedure updated.
            if (procedure != null) {
              runWhenAny(
                message,
                [
                  modelIdMatch('procedure', procedure.id),
                  allRelatedMatch('procedureversion', {
                    content_type: 'procedure',
                    id: procedure.id,
                  }),
                ],
                () => {
                  fetchTaskinstanceData();
                  fetchProcedureData();
                }
              );
            }
          },
          [task?.id]
        );

        const fetchTaskinstanceData = whenVisible(() => refetch());

        const fetchProcedureData = whenVisible(() => fetchProcedure());

        function handleDiagramStepClick(stepId: string) {
          const stepNode = document.getElementById(stepId);

          if (stepNode != null) {
            stepNode.scrollIntoView({behavior: 'smooth'});
          }
        }

        if (taskCore == null || task == null) {
          return <DetailLoadingPage />;
        }

        const normalizedSteps = normalizeSteps(
          taskCore.hasVersion === true ? taskCore.version.content : []
        );

        return (
          <CollapsedStepsState
            stepsIds={
              taskCore.hasVersion === true ? taskCore.state.orderedIds : []
            }
          >
            {({
              collapsedSteps,
              numCollapsedSteps,
              toggleAllStepsCollapse,
              toggleStepCollapse,
            }) => (
              <SPPage
                pageTitle={pageTitle}
                breadcrumbProps={{
                  docName: taskName,
                  link: task.html_url,
                  innerTaskName:
                    params.processid != null ? taskCore.titles[0] : null,
                }}
                horizontalPadding={false}
                actionArea={
                  <TaskPageActionButtons
                    taskCore={taskCore}
                    object={procedure}
                    numCollapsedSteps={numCollapsedSteps}
                    onToggleAllStepsCollapse={toggleAllStepsCollapse}
                    stepsLength={
                      taskCore.hasVersion === true
                        ? taskCore.version.content.length
                        : 0
                    }
                  />
                }
                sidebar={
                  <TaskDetailPageSidebar
                    objectContentType="procedure"
                    object={procedure}
                    task={task}
                    router={router}
                    route={route}
                  />
                }
                alternativeOverlayContent={
                  showOverlayDiagram && taskCore.hasVersion === true ? (
                    <ProcedureDiagramMobile
                      toggleDiagram={() =>
                        setShowOverlayDiagram((show) => !show)
                      }
                      steps={normalizedSteps}
                    />
                  ) : null
                }
              >
                <VideoManagerProvider>
                  <DocumentPresenceMembers
                    contentType="taskinstance"
                    objectId={task.id}
                    pageContext="view"
                    containerClass={cs('col-12', {
                      'col-md-8': !closedSideDiagram,
                    })}
                  />
                  <PresenceSubscriber
                    contentType={task.template.assigned_object.content_type}
                    objectsIds={[task.template.assigned_object.id]}
                    pageContext={'task'}
                    additionalData={{
                      contentType: 'taskinstance',
                      objectId: task.id,
                      html_url: task.html_url,
                      name: taskName,
                      description: task.description,
                    }}
                  />
                  <ViewTaskMetaData
                    taskCore={taskCore}
                    isTaskBlocked={isTaskBlocked}
                    warningComponent={
                      params.processid == null
                        ? showTaskUpgradeAvailableAlertBoxIfAvailable(
                            task,
                            taskDeleted
                          )
                        : null
                    }
                    procedure={procedure}
                    setWarnIncomplete={setWarnIncomplete}
                    updateTaskInstance={preparedUpdateTaskInstance}
                  />

                  {taskCore.hasVersion === false ? (
                    <ListMessage gray={true}>
                      {gt(
                        'Procedure version attached to this task has been deleted'
                      )}
                    </ListMessage>
                  ) : (
                    <StyledStepsListDIV className="pt-0">
                      <div className="row">
                        <div
                          className={cs(
                            'col',
                            'pt-4',
                            !closedSideDiagram && !printing
                              ? 'col-md-8'
                              : 'col-12'
                          )}
                        >
                          <ViewStepsModeTitle
                            numCollapsedSteps={numCollapsedSteps}
                            toggleAllStepsCollapse={toggleAllStepsCollapse}
                          />
                          <TaskDetailStepsList
                            taskCore={taskCore}
                            isTaskBlocked={isTaskBlocked}
                            shouldConfirmOnLeave={shouldConfirmOnLeave}
                            setWarnIncomplete={setWarnIncomplete}
                            warnIncomplete={warnIncomplete}
                            collapsedSteps={collapsedSteps}
                            toggleStepCollapse={toggleStepCollapse}
                            updateTaskInstance={preparedUpdateTaskInstance}
                          />
                          <ProcessNavButtons taskCore={taskCore} />
                        </div>
                        {!showOverlayDiagram && taskCore.hasVersion === true ? (
                          <ProcedureDiagramSidebar
                            onStepClick={handleDiagramStepClick}
                            className="d-print-none col-4 mt-4"
                            versionId={taskCore.version.id}
                            versionContentType="procedureversion"
                            showingSidebarDiagram={!closedSideDiagram}
                            toggleSidebarDiagram={() =>
                              actions.setSideDiagramState(
                                procedure.id,
                                !closedSideDiagram
                              )
                            }
                            toggleOverlayDiagram={toggleDiagramOverlay}
                            steps={normalizedSteps}
                          />
                        ) : null}
                      </div>
                      <ToggleProcedureDiagramMobileButton
                        showingDiagram={false}
                        toggleDiagram={toggleDiagramOverlay}
                      />
                    </StyledStepsListDIV>
                  )}
                </VideoManagerProvider>
              </SPPage>
            )}
          </CollapsedStepsState>
        );
      },
      [
        'hashid',
        'taskid',
        'task',
        'version',
        'route',
        'router',
        'params',
        'procedureContent',
        'procedure',
      ]
    )
  )
);

const taskDataSelector = createSelector<
  [
    (state: DefaultRootState, extra: {procedureId: number}) => number,
    (state: DefaultRootState) => AuthenticatedUserSerializedType,
    (state: DefaultRootState) => Map<string, boolean>
  ],
  {
    timezone: string;
    num_accounts: number;
    closedSideDiagram: boolean;
  }
>(
  [
    (state, {procedureId}) => procedureId,
    (state) => CurrentUserSelector(state).data!,
    (state) => state.proceduresPage.get('closedSideDiagrams'),
  ],
  (
    procedureId,
    {timezone, num_accounts, is_diagrams_expanded},
    closedSideDiagrams
  ) => {
    return {
      timezone,
      num_accounts,
      closedSideDiagram: closedSideDiagrams.get(
        procedureId.toString(),
        !is_diagrams_expanded
      ),
    };
  }
);

export const InnerTaskDetailBreadcrumb = ({
  innerTaskName,
  ...other
}: {
  innerTaskName: string;
}): JSX.Element => (
  <BreadcrumbItemStructure shrinkText={true} text={innerTaskName} {...other} />
);
